import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Table, Spin, Tag, Space, Collapse } from 'antd';
import styled from 'styled-components';
import { MakeApiCall } from '../../../../apis';
import { ConvertParams } from '../../../../config';

dayjs.extend(customParseFormat);
const { Panel } = Collapse;

// --- CONFIGURATION ---
const METRIC_CATEGORIES = {
  SALES: { label: 'Sales Metrics', color: '#f5fffb', textColor: '#50cd89', keys: ['sales.total', 'sales.organic', 'sales.sp_sales', 'sales.sb_sales', 'sales.sd_sales', 'total_orders', 'units.total', 'units.organic', 'units.sp_units', 'units.sb_units', 'units.sd_units', 'refund_units', 'sellable_returns'] },
  ADS: { label: 'Ads Metrics', color: '#f1faff', textColor: '#009ef7', keys: ['advertising_clicks.total', 'advertising_clicks.sp_click', 'advertising_clicks.sb_click', 'advertising_clicks.sd_click', 'ad_cvr', 'advertising_cost.total', 'advertising_cost.sp_cost', 'advertising_cost.sb_cost', 'advertising_cost.sd_cost', 'advertising_sales.total', 'advertising_sales.sp_sales', 'advertising_sales.sb_sales', 'advertising_sales.sd_sales'] },
  COSTS: { label: 'Costs', color: '#fff5f8', textColor: '#f1416c', keys: ['amazon_fees.total', 'amazon_fees.fba_per_unit_fulfilment_fee', 'amazon_fees.fba_weight_based_fee', 'amazon_fees.referral_fee', 'amazon_fees.fixed_closing_fee', 'amazon_fees.shipping_credits', 'amazon_fees.promotional_rebates', 'amazon_fees.item_TDS', 'cost_of_goods.total', 'refund_cost.total', 'expenses.total', 'tax_liability.total', 'tax_liability.total_tax_collected', 'tax_liability.total_tax_paid'] },
  PROFITS: { label: 'Profits', color: '#f8f5ff', textColor: '#7239ea', keys: ['gross_profit', 'net_profit'] },
};

const PL_CONFIG = [
  { key: 'sales.total', label: 'Total Sales', isParent: true },
  { key: 'sales.organic', label: 'Organic Sales' },
  { key: 'sales.sp_sales', label: 'SP Sales' },
  { key: 'sales.sb_sales', label: 'SB Sales' },
  { key: 'sales.sd_sales', label: 'SD Sales' },
  { key: 'total_orders', label: 'Total Orders' },
  { key: 'units.total', label: 'Total Units', isParent: true },
  { key: 'units.organic', label: 'Organic Units' },
  { key: 'units.sp_units', label: 'SP Units' },
  { key: 'units.sb_units', label: 'SB Units' },
  { key: 'units.sd_units', label: 'SD Units' },
  { key: 'refund_units', label: 'Refund Units' },
  { key: 'sellable_returns', label: 'Sellable Returns' },
  { key: 'advertising_clicks.total', label: 'Ad Clicks' , isParent: true },
  { key: 'advertising_clicks.sp_click', label: 'SP Clicks' },
  { key: 'advertising_clicks.sb_click', label: 'SB Clicks' },
  { key: 'advertising_clicks.sd_click', label: 'SD Clicks' },
  { key: 'ad_cvr', label: 'Ad CVR' },
  { key: 'advertising_cost.total', label: 'Ad Cost' , isParent: true },
  { key: 'advertising_cost.sp_cost', label: 'SP Cost' },
  { key: 'advertising_cost.sb_cost', label: 'SB Cost' },
  { key: 'advertising_cost.sd_cost', label: 'SD Cost' },
  { key: 'advertising_sales.total', label: 'Ad Sales' , isParent: true },
  { key: 'advertising_sales.sp_sales', label: 'SP Sales' },
  { key: 'advertising_sales.sb_sales', label: 'SB Sales' },
  { key: 'advertising_sales.sd_sales', label: 'SD Sales' },
  { key: 'amazon_fees.total', label: 'Amazon Fees', isParent: true },
  { key: 'amazon_fees.fba_per_unit_fulfilment_fee', label: 'FBA Per Unit Fee' },
  { key: 'amazon_fees.fba_weight_based_fee', label: 'FBA Weight Fee' },
  { key: 'amazon_fees.referral_fee', label: 'Referral Fee' },
  { key: 'amazon_fees.fixed_closing_fee', label: 'Fixed Closing Fee' },
  { key: 'amazon_fees.shipping_credits', label: 'Shipping Credits' },
  { key: 'amazon_fees.promotional_rebates', label: 'Promotional Rebates' },
  { key: 'amazon_fees.item_TDS', label: 'Item TDS' },
  { key: 'cost_of_goods.total', label: 'COGS' },
  { key: 'refund_cost.total', label: 'Refund Cost' },
  { key: 'expenses.total', label: 'Expenses' },
  { key: 'tax_liability.total', label: 'Tax Liability', isParent: true },
  { key: 'tax_liability.total_tax_collected', label: 'Tax Collected' },
  { key: 'tax_liability.total_tax_paid', label: 'Tax Paid' },
  { key: 'gross_profit', label: 'Gross Profit' },
  { key: 'net_profit', label: 'Net Profit' }
];

// --- STYLED COMPONENTS ---
const Wrapper = styled.div`
  .card-custom { border-radius: 12px; border: 1px solid #eff2f5; background: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
  .filter-tag { cursor: pointer; padding: 6px 14px; border-radius: 6px; font-weight: 600; transition: all 0.2s ease; border: 1px solid transparent; user-select: none; &:hover { opacity: 0.85; transform: translateY(-1px); } &.inactive { background: #f5f8fa !important; color: #a1a5b7 !important; border-color: #e4e6ef; opacity: 0.6; } }
  @media (max-width: 768px) {
    .filter-tag {
      font-size: 11px;
      padding: 4px 10px;
    }
  }
  /* Table Color Logic */
  .row-sales .ant-table-cell { background-color: #f5fffb !important; }
  .row-ads .ant-table-cell { background-color: #f1faff !important; }
  .row-costs .ant-table-cell { background-color: #fff5f8 !important; }
  .row-profits .ant-table-cell { background-color: #f8f5ff !important; }

  .trend-box { display: flex; align-items: center; justify-content: flex-end; gap: 6px; }
  .trend-arrow { font-size: 10px; padding: 2px; border-radius: 4px; &.up { color: #50cd89; background: #e8fff3; } &.down { color: #f1416c; background: #fff5f8; } }

  .mobile-date-strip { display: flex; overflow-x: auto; padding: 10px 0; scrollbar-width: none; &::-webkit-scrollbar { display: none; } -webkit-overflow-scrolling: touch; }
  .mobile-date-item { min-width: 105px; text-align: center; border-right: 1px solid #f1f1f1; padding: 0 10px; white-space: nowrap; &:last-child { border-right: none; } }
  .negative-val { color: #f1416c; white-space: nowrap; }
  .mobile-card { border: 1px solid #eff2f5; border-radius: 10px; margin-bottom: 15px; overflow: hidden; background: #fff; }

  /* Hierarchy Visuals Desktop */
  .ant-table-row-level-1 td:first-child { padding-left: 48px !important; position: relative; color: #7e8299; font-weight: 400; }
  .ant-table-row-level-1 td:first-child::before { content: ""; position: absolute; left: 30px; top: 0; bottom: 55%; width: 12px; border-left: 1.5px solid #e4e6ef; border-bottom: 1.5px solid #e4e6ef; border-bottom-left-radius: 4px; }
  .parent-row-bold { td { font-weight: 700 !important; color: #181c32 !important; } }
  .total-column-sticky { font-weight: 700 !important; border-left: 2px solid #eff2f5 !important; }
`;

// --- UTILS & HELPERS ---
const formatValue = (value, key, currencySign) => {
  if (value === null || value === undefined) return '-';
  const percentageFields = ['unit_session_percent', 'real_acos', 'refunds_percent', 'sellable_returns', 'margin', 'ad_cvr'];
  const integerFields = ['units', 'total_orders', 'sessions', 'active_subscriptions', 'advertising_clicks', 'refund_units'];
  const cleanKey = String(key).toLowerCase();

  let formatted = '';
  let isNegative = false;

  if (cleanKey.endsWith('roi')) {
    const num = Number(value);
    isNegative = num < 0;
    formatted = `${isNegative ? '-' : ''}${currencySign}${Math.abs(num).toFixed(2)}`;
  } else if (percentageFields.some(field => cleanKey.endsWith(field))) {
    const num = parseFloat(value);
    isNegative = num < 0;
    formatted = cleanKey.includes('ad_cvr') ? `${num.toFixed(2)}%` : `${(num * 100).toFixed(0)}%`;
  } else if (integerFields.some(field => cleanKey.endsWith(field) || cleanKey.includes('units') || cleanKey.includes('click'))) {
    isNegative = value < 0;
    formatted = parseInt(value).toLocaleString();
  } else if (typeof value === 'number') {
    isNegative = value < 0;
    formatted = `${isNegative ? '-' : ''}${currencySign}${Math.abs(value).toFixed(2)}`;
  } else {
    formatted = value;
  }

  return isNegative ? <span className="negative-val">{formatted}</span> : formatted;
};

const renderTrendValue = (current, previous, formattedValue) => {
  if (typeof current !== 'number' || typeof previous !== 'number') return formattedValue;
  const isUp = current > previous;
  const isEqual = current === previous;
  return (
      <div className="trend-box">
        {!isEqual && (
            <span className={`trend-arrow ${isUp ? 'up' : 'down'}`}>
          {isUp ? '▲' : '▼'}
        </span>
        )}
        <span>{formattedValue}</span>
      </div>
  );
};

const isBoldRow = (record) => {
  const key = record.key;
  return record._isParentConfig || !key.includes('.') || key.endsWith('.total') || key === 'gross_profit' || key === 'net_profit';
};

const flattenData = (obj, prefix = '') => {
  return Object.entries(obj).reduce((acc, [key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (val && typeof val === 'object' && !Array.isArray(val)) { Object.assign(acc, flattenData(val, path)); }
    else { acc[path] = val; }
    return acc;
  }, {});
};

// --- COMPONENTS ---
const PLMobileCard = ({ row, dateKeys, selectedCurrencySign, getRowClassName, onScroll, registerRef }) => {
  const categoryClass = getRowClassName(row);
  return (
      <div className={`mobile-card ${categoryClass}`}>
        <div className="px-4 py-3 d-flex justify-content-between align-items-center border-bottom bg-white bg-opacity-50">
          <span className="fw-bold text-dark">{row.parameter}</span>
          <span className="badge bg-white text-dark shadow-sm border">
          {formatValue(row.total, row.key, selectedCurrencySign)}
        </span>
        </div>
        <div className="mobile-date-strip bg-white" ref={registerRef} onScroll={onScroll}>
          {dateKeys.map((date, idx) => {
            const val = row[date];
            const prevVal = row[dateKeys[idx + 1]];
            return (
                <div key={date} className="mobile-date-item">
                  <div style={{ fontSize: '10px' }} className="text-muted text-uppercase mb-1">{date}</div>
                  <div style={{ fontSize: '13px' }} className="fw-bold">
                    {renderTrendValue(val, prevVal, formatValue(val, row.key, selectedCurrencySign))}
                  </div>
                </div>
            );
          })}
        </div>
        {row.children && (
            <Collapse ghost className="border-top">
              <Panel header={<span className="text-muted fs-9 fw-bold px-2">VIEW BREAKDOWN</span>} key="1">
                {row.children.map(child => (
                    <div key={child.key} className="px-3 py-3 border-bottom bg-white bg-opacity-25">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fs-8 fw-bold text-gray-700">{child.parameter}</span>
                        <span className="fs-8 fw-bold">{formatValue(child.total, child.key, selectedCurrencySign)}</span>
                      </div>
                      <div className="mobile-date-strip" onScroll={onScroll} ref={(el) => registerRef(el)}>
                        {dateKeys.map((date) => (
                            <div key={date} className="mobile-date-item">
                              <span style={{fontSize: '11px'}}>{formatValue(child[date], child.key, selectedCurrencySign)}</span>
                            </div>
                        ))}
                      </div>
                    </div>
                ))}
              </Panel>
            </Collapse>
        )}
      </div>
  );
};

// --- DATA HOOK ---
const useProfitAndLossData = (filters, token, selectedCurrencySign) => {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [dateKeys, setDateKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const buildTable = useCallback((data) => {
    if (!data?.length) return;
    const uniqueDates = Array.from(new Set(data.map(entry => entry.report_date))).filter(Boolean);
    const parseToMillis = (raw) => {
      if (!raw) return 0;
      let str = String(raw).trim().replace(/[\/\-]+/g, ' ').replace(/\s+/g, ' ');
      const formats = ['MMM YYYY', 'YYYY-MM-DD', 'YYYY-MM', 'YYYY'];
      for (const fmt of formats) {
        const d = dayjs(str, fmt, true);
        if (d.isValid()) return d.valueOf();
      }
      return dayjs(str).isValid() ? dayjs(str).valueOf() : 0;
    };
    const sortedDates = uniqueDates.sort((a, b) => parseToMillis(b) - parseToMillis(a));
    setDateKeys(sortedDates);

    const dynamicColumns = [
      { title: 'Parameter', dataIndex: 'parameter', key: 'parameter', fixed: 'left', width: 260, render: (text, record) => <span style={{ fontWeight: isBoldRow(record) ? '700' : '400' }}>{text}</span> },
      ...sortedDates.map((date, index) => ({
        title: date, dataIndex: date, key: date, align: 'right', width: 150,
        render: (value, record) => renderTrendValue(value, record[sortedDates[index+1]], formatValue(value, record.key, selectedCurrencySign))
      })),
      { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', width: 160, fixed: 'right', className: 'total-column-sticky', render: (value, record) => formatValue(value, record.key, selectedCurrencySign) },
    ];

    const dateMap = data.reduce((acc, item) => { acc[item.report_date] = flattenData(item); return acc; }, {});
    const rowList = PL_CONFIG.map(config => {
      const row = { key: config.key, parameter: config.label, _isParentConfig: config.isParent };
      let sum = 0, count = 0;
      sortedDates.forEach(date => {
        let val = (config.key === 'ad_cvr') ?
            (((dateMap[date]?.['units.sp_units'] || 0) + (dateMap[date]?.['units.sb_units'] || 0) + (dateMap[date]?.['units.sd_units'] || 0)) / (dateMap[date]?.['advertising_clicks.total'] || 1) * 100) :
            dateMap[date]?.[config.key];
        row[date] = val;
        if (typeof val === 'number') { sum += val; count++; }
      });
      row.total = (config.key === 'ad_cvr') ? sum/count : sum; // Simplified for brevity
      return row;
    });

    const finalRows = [];
    const parentLookup = {};
    rowList.forEach(row => {
      if (row._isParentConfig) { row.children = []; parentLookup[row.key.split('.')[0]] = row; finalRows.push(row); }
      else {
        const prefix = row.key.split('.')[0];
        if (parentLookup[prefix]) parentLookup[prefix].children.push(row);
        else finalRows.push(row);
      }
    });

    setColumns(dynamicColumns);
    setRows(finalRows);
  }, [selectedCurrencySign]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await MakeApiCall(`dashboard-optimized/profit-and-loss/get-summary-data${ConvertParams(filters)}`, 'get', null, true, {}, token);
      if (res?.status) buildTable(res.data?.summaryData || []);
      setLoading(false);
    };
    fetchData();
  }, [JSON.stringify(filters), token, buildTable]);

  return { columns, rows, loading, dateKeys };
};

// --- MAIN PL COMPONENT ---
const PLV2 = ({ periodicity, otherRange, userData, selectedMarketplace, selectedCurrencySign, selectedSku }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const scrollRefs = useRef([]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSyncScroll = useCallback((e) => {
    const scrollLeft = e.target.scrollLeft;
    scrollRefs.current.forEach(ref => { if (ref && ref !== e.target) ref.scrollLeft = scrollLeft; });
  }, []);

  const registerRef = useCallback((el) => { if (el && !scrollRefs.current.includes(el)) scrollRefs.current.push(el); }, []);

  const filters = useMemo(() => ({
    page: 1, 'per-page': 20, marketplace_id: selectedMarketplace?.id, report_type: periodicity, start_date: otherRange?.start_date, end_date: otherRange?.end_date, sku_lists: selectedSku?.join(",")
  }), [selectedMarketplace?.id, periodicity, otherRange, selectedSku]);

  const { columns, rows, loading, dateKeys } = useProfitAndLossData(filters, userData?.token, selectedCurrencySign);
  const [activeCategories, setActiveCategories] = useState(['SALES', 'ADS', 'COSTS', 'PROFITS']);

  const getRowClassName = (record) => {
    if (METRIC_CATEGORIES.SALES.keys.includes(record.key)) return 'row-sales';
    if (METRIC_CATEGORIES.ADS.keys.includes(record.key)) return 'row-ads';
    if (METRIC_CATEGORIES.COSTS.keys.includes(record.key)) return 'row-costs';
    if (METRIC_CATEGORIES.PROFITS.keys.includes(record.key)) return 'row-profits';
    return '';
  };

  const filteredRows = useMemo(() => {
    const allowedKeys = activeCategories.flatMap(cat => METRIC_CATEGORIES[cat].keys);
    return rows.filter(row => allowedKeys.includes(row.key) || (row.children?.some(c => allowedKeys.includes(c.key))));
  }, [rows, activeCategories]);

  return (
      <Wrapper>
        <div className="card-custom">
          <div className="d-flex flex-column flex-md-row justify-content-between px-8 py-6 border-bottom">
            <h3 className="m-0 fw-bold">Profit & Loss</h3>
            <Space className="mt-4 mt-md-0"  wrap>
              {Object.entries(METRIC_CATEGORIES).map(([key, config]) => (
                  <Tag key={key} className={`filter-tag ${activeCategories.includes(key) ? '' : 'inactive'}`} style={{ background: activeCategories.includes(key) ? config.color : '', color: config.textColor }} onClick={() => setActiveCategories(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])}>
                    {config.label}
                  </Tag>
              ))}
            </Space>
          </div>
          <div className="p-2">
            {loading ? <div className="text-center py-20"><Spin size="large" /></div> : isMobile ? (
                <div>{filteredRows.map(row => <PLMobileCard key={row.key} row={row} dateKeys={dateKeys} selectedCurrencySign={selectedCurrencySign} getRowClassName={getRowClassName} onScroll={handleSyncScroll} registerRef={registerRef} />)}</div>
            ) : (
                <Table scroll={{ x: 'max-content' }} columns={columns} dataSource={filteredRows} pagination={false} rowClassName={(r) => `${getRowClassName(r)} ${isBoldRow(r) ? 'parent-row-bold' : ''}`} />
            )}
          </div>
        </div>
      </Wrapper>
  );
};

export default PLV2;
